import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, NavLink, useLocation, Navigate } from 'react-router-dom';

// GenUI Toast Provider - Global notification system (ADR-074)
import { ToastProvider } from './components/genui/components/Toast';

import { LEGAL_REDIRECTS } from './config/legalUrls';
import {
  isPublicSurfacePath as isPublicProductExperiencePath,
  isReservedReportMarketingShape,
} from './config/publicSurfaces';

// Redirect local legal routes to 44laboratories.com/legal/*
const ExternalRedirect = ({ to }) => {
  useEffect(() => { window.location.href = to; }, [to]);
  return null;
};

import { useAuth } from './contexts/AuthContext';
import { signOut } from 'firebase/auth';
import { auth } from './firebase-config';
import { signOutAndRedirectToLogin } from './utils/logoutFlow';
import './utils/errorLogger';
import { captureUtmParams } from './utils/utm';
import { isNonProductionEnvironment } from './utils/environment';
import { resolveAuthContinuePath } from './utils/authContinueUrl';
import Footer from './components/Footer';
import DevInfo from './components/DevInfo';
import AppShell from './components/AppShell';
import { GlobalComposerSheet } from './components/layout/GlobalComposerSheet';
import PrivateRoute from './components/PrivateRoute';
import EntitlementBasedRoute from './components/EntitlementBasedRoute';
import PurchaseConversionTracker from './components/PurchaseConversionTracker';
import ReportMarketingRoute from './components/public/ReportMarketingRoute';

// Eager load critical components
import Landing from './pages/Landing';
import Assessment from './pages/Assessment';
import AssessmentV3 from './pages/AssessmentV3';
import AssessmentWorking from './pages/AssessmentWorking';
import AssessmentAug10 from './pages/AssessmentAug10';
import AssessmentResults from './components/AssessmentResults';
// Removed AssessmentResult - using AssessmentResults component instead

// Lazy load non-critical components
const MyAssessments = lazy(() => import('./pages/MyAssessments'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const Studio = lazy(() => import('./pages/Studio'));
const StudioNew = lazy(() => import('./pages/StudioNew'));
const LensNew = lazy(() => import('./pages/LensNew'));
const LensFullModal = lazy(() => import('./pages/LensFullModal'));
const HistoricalReportViewer = lazy(() => import('./pages/HistoricalReportViewer'));
const SharedReportPage = lazy(() => import('./pages/SharedReportPage'));
const Learn = lazy(() => import('./pages/Learn'));
const JargonBuster = lazy(() => import('./pages/JargonBuster'));
const PrintableWorkbooks = lazy(() => import('./pages/PrintableWorkbooks'));
const SelfGuidedQuizzes = lazy(() => import('./pages/SelfGuidedQuizzes'));
const CuratedResources = lazy(() => import('./pages/CuratedResources'));
const QuickTips = lazy(() => import('./pages/QuickTips'));
const PlatformFAQs = lazy(() => import('./pages/PlatformFAQs'));
const Unauthorized = lazy(() => import('./pages/Unauthorized'));
// Pricing route variants are environment-gated below.
const Pricing = lazy(() => import('./pages/Pricing'));
const PricingFull = lazy(() => import('./pages/PricingFull'));  // Note: different from PricingFullTailwind
const PricingFullTailwind = lazy(() => import('./pages/PricingFullTailwind'));
const PricingTable = lazy(() => import('./pages/PricingTable'));
const PricingCards = lazy(() => import('./pages/PricingCards'));  // Note: different from PricingCardsNew
const PricingCardsNew = lazy(() => import('./pages/PricingCardsNew'));
const PricingStripe = lazy(() => import('./pages/PricingStripe')); // NEW: Stripe Pricing Table
const PricingTableUI = lazy(() => import('./pages/PricingTableUI')); // NEW: Untitled UI Pricing Table
const Checkout = lazy(() => import('./pages/Checkout'));
const UpgradeQuestionnaire = lazy(() => import('./pages/UpgradeQuestionnaire'));
const Dash = lazy(() => import('./pages/Dash'));
const AccountSettings = lazy(() => import('./pages/AccountSettings'));
const UIShowcase = lazy(() => import('./pages/UIShowcase'));
const UISandbox = lazy(() => import('./pages/UISandbox'));
import SandboxRouter from './components/SandboxRouter';
import PaymentSuccess from './pages/PaymentSuccess';
const PaymentSuccessSetup = lazy(() => import('./pages/PaymentSuccessSetup'));
const PaymentCallback = lazy(() => import('./pages/PaymentCallback'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const TestUserRole = lazy(() => import('./pages/TestUserRole'));
const ReportUITest = lazy(() => import('./pages/ReportUITest'));
const FixUserRole = lazy(() => import('./pages/FixUserRole'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const Support = lazy(() => import('./pages/Support'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const AuthAction = lazy(() => import('./pages/AuthAction'));
const LensTesting = lazy(() => import('./pages/LensTesting'));
const ReportGenerationLoader = lazy(() => import('./pages/ReportGenerationLoader'));
const EmailDesign = lazy(() => import('./pages/EmailDesign'));
const SimpleEmailPreview = lazy(() => import('./pages/SimpleEmailPreview'));
const TestSimpleReport = lazy(() => import('./pages/TestSimpleReport'));
const DemoT2T3 = lazy(() => import('./pages/DemoT2T3'));
const LensDemo = lazy(() => import('./pages/LensDemo'));
const GetStarted = lazy(() => import('./pages/GetStarted'));
const CampaignLandingPage = lazy(() => import('./pages/CampaignLandingPage'));
const EditorialReportDestination = lazy(() => import('./pages/Landing/EditorialReportDestination'));
const WelcomeCapture = lazy(() => import('./pages/WelcomeCapture'));
const PotentialUnlocked = lazy(() => import('./pages/PotentialUnlocked'));
const TestReportV3 = lazy(() => import('./pages/TestReportV3'));
const TestReportGeneration = lazy(() => import('./pages/TestReportGeneration'));
const ReportUI = lazy(() => import('./pages/ReportUI'));
const ProjectFactsWorkspace = lazy(() => import('./pages/ProjectFactsWorkspace'));
const ReportProjectNavigator = lazy(() => import('./pages/ReportProjectNavigator'));
const ReportInsightStudio = lazy(() => import('./pages/ReportInsightStudio'));
const FourtyFourLabsUI = lazy(() => import('./pages/44LabsUI'));
const TestPaymentModal = lazy(() => import('./pages/TestPaymentModal'));
const UIKit = lazy(() => import('./pages/UIKit'));
const DesignSystem = lazy(() => import('./pages/DesignSystem'));
const HomepageLandingPage = lazy(() => import('./pages/HomepageLandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));

const LoginRoute = ({ currentUser }) => {
  const location = useLocation();
  const continuePath = resolveAuthContinuePath(new URLSearchParams(location.search).get('continueUrl'));

  return currentUser ? <Navigate to={continuePath} replace /> : <LoginPage />;
};

const DoNotSharePage = lazy(() => import('./pages/legal/DoNotShare'));
const PrivacyPage = lazy(() =>
  import('./pages/legal/Privacy').then((m) => ({ default: m.PrivacyPage }))
);
const CookiePolicyPage = lazy(() =>
  import('./pages/legal/CookiePolicy').then((m) => ({ default: m.CookiePolicyPage }))
);
// Wave 2 legal pages — all self-hosted at /legal/* (no external redirects).
const LegalHubPage = lazy(() =>
  import('./pages/legal/LegalHub').then((m) => ({ default: m.LegalHub }))
);
const TermsPage = lazy(() =>
  import('./pages/legal/Terms').then((m) => ({ default: m.TermsPage }))
);
const ServiceTermsPage = lazy(() =>
  import('./pages/legal/ServiceTerms').then((m) => ({ default: m.ServiceTermsPage }))
);
const PaymentTermsPage = lazy(() =>
  import('./pages/legal/PaymentTerms').then((m) => ({ default: m.PaymentTermsPage }))
);
const RefundPolicyPage = lazy(() =>
  import('./pages/legal/RefundPolicy').then((m) => ({ default: m.RefundPolicyPage }))
);
const AdvertisingTermsPage = lazy(() =>
  import('./pages/legal/AdvertisingTerms').then((m) => ({ default: m.AdvertisingTermsPage }))
);
const DataProcessingPage = lazy(() =>
  import('./pages/legal/DataProcessing').then((m) => ({ default: m.DataProcessingPage }))
);
const RegionalPrivacyPage = lazy(() =>
  import('./pages/legal/RegionalPrivacy').then((m) => ({ default: m.RegionalPrivacyPage }))
);
const EmailDisclaimerPage = lazy(() =>
  import('./pages/legal/EmailDisclaimer').then((m) => ({ default: m.EmailDisclaimerPage }))
);
const AssessmentDisclaimerPage = lazy(() =>
  import('./pages/legal/AssessmentDisclaimer').then((m) => ({ default: m.AssessmentDisclaimerPage }))
);
const ReportsDisclaimerPage = lazy(() =>
  import('./pages/legal/ReportsDisclaimer').then((m) => ({ default: m.ReportsDisclaimerPage }))
);
const AIDisclosurePage = lazy(() =>
  import('./pages/legal/AIDisclosure').then((m) => ({ default: m.AIDisclosurePage }))
);
const DMCAPage = lazy(() =>
  import('./pages/legal/DMCA').then((m) => ({ default: m.DMCAPage }))
);
const TrustSafetyPage = lazy(() =>
  import('./pages/legal/TrustSafety').then((m) => ({ default: m.TrustSafetyPage }))
);
// const ADULabShell = lazy(() => import('./pages/ADULabShell'));
const DevPrimitivesIndex = lazy(() => import('./pages/dev/primitives/DevPrimitivesIndex'));
const PrimitivePreview = lazy(() => import('./pages/dev/primitives/PrimitivePreview'));
const ChatUITriggersIndex = lazy(() => import('./pages/dev/chat-ui-triggers/ChatUITriggersIndex'));
const SmartToolCreditWiring = lazy(() => import('./pages/dev/SmartToolCreditWiring'));
const EmailAutomation = lazy(() => import('./pages/dev/EmailAutomation'));
const AgentActivityPreview = lazy(() => import('./pages/dev/AgentActivityPreview'));
const GenerativeUISandbox = lazy(() => import('./pages/dev/GenerativeUISandbox'));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      <p className="mt-2 text-gray-600">Loading...</p>
    </div>
  </div>
);

export const AppContent = () => {
  const { currentUser } = useAuth();
  const location = useLocation();

  const handleLogout = async () => {
    await signOutAndRedirectToLogin({ auth, signOutFn: signOut });
  };

  // All products use the same routes - branding/theming handled by AppShell
  const navlessRoutes = [
    '/login',
    '/assessment',
    '/pricing',
    '/legal/do-not-share',
    '/assessment/',
    '/forgot-password',
    '/verify-email',
    '/auth-action',
    '/landing',
    '/payment-success',
    '/payment-success-setup',
    '/payment-callback',
    '/getstarted',
    '/welcome',
    '/lp/',
    '/assessment-v3',
    '/assessment-working',
    '/assessment-aug10',
    '/ui-kit',
    '/design-system',
    '/dev/',
  ];

  const isPublicSurface =
    isPublicProductExperiencePath(location.pathname) ||
    isReservedReportMarketingShape(location.pathname);
  const isNavlessRoute =
    navlessRoutes.some(path => location.pathname.startsWith(path)) ||
    isPublicSurface;
  const isRootRoute = location.pathname === '/';
  const isPublicSupportRoute = ['/support', '/help', '/legal'].some(path => location.pathname.startsWith(path));

  // Hide nav/footer on navless routes, landing root, or when anonymous visitors hit support/help/legal
  const hideNavAndFooter = isNavlessRoute || isRootRoute || (!currentUser && isPublicSupportRoute);

  const isInternalAppPage = currentUser && !hideNavAndFooter && !['/landing', '/unauthorized', '/payment-success'].includes(location.pathname);

  return (
    <>
      {isInternalAppPage ? (
        <AppShell>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Navigate to="/dash" replace />} />
              <Route path="/login" element={<Navigate to="/dash" replace />} />
              <Route path="/studio" element={<Navigate to="/dash" replace />} />
              <Route path="/dash" element={<PrivateRoute><Dash /></PrivateRoute>} />
              <Route path="/hub" element={<PrivateRoute><Dash /></PrivateRoute>} />
              <Route path="/lens" element={<PrivateRoute><Dash /></PrivateRoute>} />
              <Route path="/lens/report" element={<PrivateRoute><LensFullModal /></PrivateRoute>} />
              <Route path="/lab" element={<PrivateRoute><Dash /></PrivateRoute>} />
              <Route path="/reports/history/:projectId/:versionId" element={<PrivateRoute><HistoricalReportViewer /></PrivateRoute>} />
              <Route path="/report-viewer/:projectId/:reportId" element={<PrivateRoute><ReportProjectNavigator /></PrivateRoute>} />
              <Route path="/report-ui" element={<PrivateRoute><ReportUI /></PrivateRoute>} />
              <Route path="/report-ui/report-project-navigator" element={<PrivateRoute><ReportProjectNavigator /></PrivateRoute>} />
              <Route path="/report-ui/report-insight-studio" element={<PrivateRoute><ReportInsightStudio /></PrivateRoute>} />
              <Route path="/44labs-ui" element={<PrivateRoute><FourtyFourLabsUI /></PrivateRoute>} />
              <Route path="/learn" element={<PrivateRoute><Learn /></PrivateRoute>} />
              <Route path="/learn/jargon-buster" element={<PrivateRoute><JargonBuster /></PrivateRoute>} />
              <Route path="/learn/printable-workbooks" element={<PrivateRoute><PrintableWorkbooks /></PrivateRoute>} />
              <Route path="/learn/self-guided-quizzes" element={<PrivateRoute><SelfGuidedQuizzes /></PrivateRoute>} />
              <Route path="/learn/curated-resources" element={<PrivateRoute><CuratedResources /></PrivateRoute>} />
              <Route path="/learn/quick-tips" element={<PrivateRoute><QuickTips /></PrivateRoute>} />
              <Route path="/account-settings" element={<PrivateRoute><AccountSettings /></PrivateRoute>} />
              {/* Test pages in AppShell - staging/dev only */}
              {isNonProductionEnvironment() && (
                <>
                  <Route path="/test-simple-report" element={<PrivateRoute><TestSimpleReport /></PrivateRoute>} />
                </>
              )}
              <Route path="/my-assessments" element={<PrivateRoute><MyAssessments /></PrivateRoute>} />
              <Route path="/report-generation" element={<PrivateRoute><ReportGenerationLoader /></PrivateRoute>} />
              <Route path="/upgrade-questionnaire" element={<PrivateRoute><UpgradeQuestionnaire /></PrivateRoute>} />
              <Route path="/checkout/:tier" element={<Checkout />} />
              {/* Keep public help routes mirrored here and in the public route tree below. */}
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/help/guides" element={<Navigate to="/learn" replace />} />
              <Route path="/help/platform-faqs" element={<PlatformFAQs />} />
              <Route path="/help/:collection" element={<HelpCenter />} />
              <Route path="/help/:collection/:article" element={<HelpCenter />} />
              <Route path="/support" element={<Support />} />
              <Route path="/admin" element={
                <EntitlementBasedRoute requireAdmin={true}>
                  <AdminPanel />
                </EntitlementBasedRoute>
              } />

              {/* Redirect auth pages to landing page */}
              <Route path="/auth" element={<Navigate to="/login" replace />} />
              <Route path="/pricing" element={<PricingCards />} />
              <Route path="/legal/do-not-share" element={<DoNotSharePage />} />
              {/* Wave 1 + Wave 2 self-hosted legal pages — in-app, no redirect */}
              <Route path="/legal" element={<LegalHubPage />} />
              <Route path="/legal/privacy" element={<PrivacyPage />} />
              <Route path="/legal/cookie-policy" element={<CookiePolicyPage />} />
              <Route path="/legal/terms" element={<TermsPage />} />
              <Route path="/legal/service-terms" element={<ServiceTermsPage />} />
              <Route path="/legal/payment-terms" element={<PaymentTermsPage />} />
              <Route path="/legal/refund-policy" element={<RefundPolicyPage />} />
              <Route path="/legal/advertising-terms" element={<AdvertisingTermsPage />} />
              <Route path="/legal/data-processing" element={<DataProcessingPage />} />
              <Route path="/legal/regional-privacy" element={<RegionalPrivacyPage />} />
              <Route path="/legal/email-disclaimer" element={<EmailDisclaimerPage />} />
              <Route path="/legal/assessment-disclaimer" element={<AssessmentDisclaimerPage />} />
              <Route path="/legal/reports-disclaimer" element={<ReportsDisclaimerPage />} />
              <Route path="/legal/ai-disclosure" element={<AIDisclosurePage />} />
              <Route path="/legal/dmca" element={<DMCAPage />} />
              <Route path="/legal/trust-safety" element={<TrustSafetyPage />} />
              {/* Legacy short paths — SPA-redirect to new locations */}
              <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
              <Route path="/cookie-policy" element={<Navigate to="/legal/cookie-policy" replace />} />
              <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
              <Route path="/service-terms" element={<Navigate to="/legal/service-terms" replace />} />
              <Route path="/payment-terms" element={<Navigate to="/legal/payment-terms" replace />} />
              <Route path="/data-processing" element={<Navigate to="/legal/data-processing" replace />} />
              <Route path="/regional-privacy" element={<Navigate to="/legal/regional-privacy" replace />} />
              <Route path="/advertising-terms" element={<Navigate to="/legal/advertising-terms" replace />} />
              <Route path="/trust-safety" element={<Navigate to="/legal/trust-safety" replace />} />
              {isNonProductionEnvironment() && <Route path="/pricing-stripe" element={<PricingStripe />} />}

              {/* Public routes that should still be accessible */}
              <Route path="/report/:projectId/:shareToken" element={<SharedReportPage />} />
              <Route path="/landing" element={<Landing />} />
              <Route path="/lp/meta-nosac-lead-a" element={<CampaignLandingPage campaign="meta-nosac-lead-a" />} />
              <Route path="/lp/meta-report-offer-b" element={<CampaignLandingPage campaign="meta-report-offer-b" />} />
              <Route path="/lp/report" element={<EditorialReportDestination />} />
              {/* Variant C's public lead-capture gateway. Its source handoff is
                  validated and short-lived; direct visits remain purchase-safe. */}
              <Route path="/welcome" element={<WelcomeCapture />} />
              <Route path="/assessment" element={<Assessment />} />
              {/* Assessment variants in AppShell - staging/dev only */}
              {isNonProductionEnvironment() && (
                <>
                  <Route path="/assessment-v3" element={<AssessmentV3 />} />
                  <Route path="/assessment-working" element={<AssessmentWorking />} />
                  <Route path="/assessment-aug10" element={<AssessmentAug10 />} />
                </>
              )}
              <Route path="/assessment/:token" element={<AssessmentResults />} />
              <Route path="/assessment/results/:id" element={<AssessmentResults />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/payment-success-setup" element={<PaymentSuccessSetup />} />
              <Route path="/unauthorized" element={<Unauthorized />} />
              {/* LEGAL_REDIRECTS retired Wave 2 — all /legal/* pages self-hosted. */}
              {Object.entries(LEGAL_REDIRECTS).map(([path, url]) => (
                <Route key={path} path={path} element={<ExternalRedirect to={url} />} />
              ))}
              {/* UI Sandbox Routes - staging/dev only */}
              {isNonProductionEnvironment() && (
                <>
                  <Route path="/ui" element={<UIShowcase />} />
                  <Route path="/ui/sandbox" element={<UISandbox />} />
                  <Route path="/ui/*" element={<SandboxRouter />} />
                </>
              )}
            </Routes>
          </Suspense>
        </AppShell>
      ) : (
        <>
          {!hideNavAndFooter && (
            <nav className="bg-white shadow-sm">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between h-16">
                  <div className="flex">
                    <Link to="/" className="flex-shrink-0 flex items-center">
                      <span className="text-2xl font-bold text-gray-800">ADU Lab</span>
                    </Link>
                  </div>
                  <div className="flex items-center space-x-4">
                    {currentUser ? (
                      <>
                        <NavLink
                          to="/dash"
                          className={({ isActive }) =>
                            isActive ? "text-blue-600 font-medium" : "text-gray-700 hover:text-gray-900"
                          }
                        >
                          Dashboard
                        </NavLink>
                        <button
                          onClick={handleLogout}
                          className="text-gray-700 hover:text-gray-900"
                        >
                          Logout
                        </button>
                      </>
                    ) : (
                      <Link
                        to="/login"
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                      >
                        Sign In
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </nav>
          )}

          <div className="min-h-screen">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={currentUser ? <Navigate to="/dash" replace /> : <HomepageLandingPage />} />
                <Route path="/login" element={<LoginRoute currentUser={currentUser} />} />
                <Route path="/landing" element={<Landing />} />
                <Route path="/assessment" element={<Assessment />} />
                {/* Assessment variants - staging/dev only */}
                {/* /adu-classic RETIRED (Derek 2026-08-27): the legacy LandingPage carried
                    testimonial content that conflicts with the site-wide no-fabricated-
                    social-proof decision. Do not re-route it. */}
                {isNonProductionEnvironment() && (
                  <>
                    <Route path="/assessment-v3" element={<AssessmentV3 />} />
                    <Route path="/assessment-working" element={<AssessmentWorking />} />
                    <Route path="/assessment-aug10" element={<AssessmentAug10 />} />
                  </>
                )}
                <Route path="/assessment/:token" element={<AssessmentResults />} />
                <Route path="/assessment/results/:id" element={<AssessmentResults />} />
                <Route path="/auth" element={<Navigate to="/login" replace />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/auth-action" element={<AuthAction />} />
                <Route path="/payment-callback" element={<PaymentCallback />} />
                {/* Production pricing route */}
                <Route path="/pricing" element={<PricingCards />} />
                <Route path="/legal/do-not-share" element={<DoNotSharePage />} />
                {/* Wave 1 + Wave 2 self-hosted legal pages — in-app, no redirect */}
                <Route path="/legal" element={<LegalHubPage />} />
                <Route path="/legal/privacy" element={<PrivacyPage />} />
                <Route path="/legal/cookie-policy" element={<CookiePolicyPage />} />
                <Route path="/legal/terms" element={<TermsPage />} />
                <Route path="/legal/service-terms" element={<ServiceTermsPage />} />
                <Route path="/legal/payment-terms" element={<PaymentTermsPage />} />
                <Route path="/legal/refund-policy" element={<RefundPolicyPage />} />
                <Route path="/legal/advertising-terms" element={<AdvertisingTermsPage />} />
                <Route path="/legal/data-processing" element={<DataProcessingPage />} />
                <Route path="/legal/regional-privacy" element={<RegionalPrivacyPage />} />
                <Route path="/legal/email-disclaimer" element={<EmailDisclaimerPage />} />
                <Route path="/legal/assessment-disclaimer" element={<AssessmentDisclaimerPage />} />
                <Route path="/legal/reports-disclaimer" element={<ReportsDisclaimerPage />} />
                <Route path="/legal/ai-disclosure" element={<AIDisclosurePage />} />
                <Route path="/legal/dmca" element={<DMCAPage />} />
                <Route path="/legal/trust-safety" element={<TrustSafetyPage />} />
                {/* Legacy short paths — SPA-redirect to new locations */}
                <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
                <Route path="/cookie-policy" element={<Navigate to="/legal/cookie-policy" replace />} />
                <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
                <Route path="/service-terms" element={<Navigate to="/legal/service-terms" replace />} />
                <Route path="/payment-terms" element={<Navigate to="/legal/payment-terms" replace />} />
                <Route path="/data-processing" element={<Navigate to="/legal/data-processing" replace />} />
                <Route path="/regional-privacy" element={<Navigate to="/legal/regional-privacy" replace />} />
                <Route path="/advertising-terms" element={<Navigate to="/legal/advertising-terms" replace />} />
                <Route path="/trust-safety" element={<Navigate to="/legal/trust-safety" replace />} />
                {/* Pricing test routes - staging/dev only */}
                {isNonProductionEnvironment() && (
                  <>
                    <Route path="/pricing-stripe" element={<PricingStripe />} />
                    <Route path="/pricing1" element={<Pricing />} />
                    <Route path="/pricing2" element={<PricingFull />} />
                    <Route path="/pricing3" element={<PricingFullTailwind />} />
                    <Route path="/pricing4" element={<PricingTable />} />
                    <Route path="/pricing5" element={<PricingCards />} />
                    <Route path="/pricing6" element={<PricingCardsNew />} />
                    <Route path="/pricing-table" element={<PricingTableUI />} />
                  </>
                )}
                <Route path="/checkout/:tier" element={<Checkout />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/payment-success-setup" element={<PaymentSuccessSetup />} />
                <Route path="/learn/*" element={<Navigate to="/login" replace />} />
                <Route path="/account-settings" element={<Navigate to="/login" replace />} />
                {/* LEGAL_REDIRECTS retired Wave 2 — all /legal/* pages self-hosted. */}
                {Object.entries(LEGAL_REDIRECTS).map(([path, url]) => (
                  <Route key={path} path={path} element={<ExternalRedirect to={url} />} />
                ))}
                {/* Demo pages - staging/dev only */}
                {isNonProductionEnvironment() && (
                  <>
                    <Route path="/demo-t2t3" element={<DemoT2T3 />} />
                    <Route path="/lens-demo" element={<LensDemo />} />
                  </>
                )}

                {/* Marketing Landing Pages */}
                <Route path="/getstarted" element={<GetStarted />} />
                <Route path="/lp/potential-unlocked" element={<PotentialUnlocked />} />
                {isNonProductionEnvironment() && <Route path="/lp/homepage-preview" element={<HomepageLandingPage />} />}
                <Route path="/lp/meta-nosac-lead-a" element={<CampaignLandingPage campaign="meta-nosac-lead-a" />} />
                <Route path="/lp/meta-report-offer-b" element={<CampaignLandingPage campaign="meta-report-offer-b" />} />
                <Route path="/lp/report" element={<EditorialReportDestination />} />
                <Route path="/report/:projectId/:shareToken" element={<SharedReportPage />} />
                <Route path="/report" element={<ReportMarketingRoute />} />
                <Route path="/report/:messageSlug" element={<ReportMarketingRoute />} />
                {/* Variant C's public lead-capture gateway. Keep this mirrored
                    with the signed-in route tree above. */}
                <Route path="/welcome" element={<WelcomeCapture />} />
                <Route path="/lp/generational-living" element={<CampaignLandingPage campaign="generational-living" />} />
                <Route path="/lp/lifestyle-studio" element={<CampaignLandingPage campaign="lifestyle-studio" />} />
                <Route path="/lp/imagine-possibility" element={<CampaignLandingPage campaign="imagine-possibility" />} />

                <Route path="/email-design" element={<EmailDesign />} />

                {/* Individual email preview routes */}
                <Route path="/email/:template" element={<SimpleEmailPreview />} />

                {/* Test/dev pages - staging/dev only */}
                {isNonProductionEnvironment() && (
                  <>
                    <Route path="/test-report-v3" element={<TestReportV3 />} />
                    <Route path="/test-report-generation" element={<TestReportGeneration />} />
                    <Route path="/test-payment-modal" element={<TestPaymentModal />} />
                    <Route path="/test-user-role" element={<PrivateRoute><TestUserRole /></PrivateRoute>} />
                    <Route path="/fix-user-role" element={<PrivateRoute><FixUserRole /></PrivateRoute>} />
                    <Route path="/ui-kit" element={<UIKit />} />
                    <Route path="/design-system" element={<DesignSystem />} />
                    <Route path="/dev/report-ui/9-category-adu" element={<ReportUITest />} />
                    <Route path="/dev/information-synthesis" element={<PrivateRoute><ProjectFactsWorkspace /></PrivateRoute>} />
                    <Route path="/dev/project-facts" element={<PrivateRoute><ProjectFactsWorkspace /></PrivateRoute>} />
                    {/* GenUI primitive previews */}
                    <Route path="/dev/primitives" element={<DevPrimitivesIndex />} />
                    <Route path="/dev/primitives/:primitive" element={<PrimitivePreview />} />
                    <Route path="/dev/chat-ui-triggers" element={<ChatUITriggersIndex />} />
                    <Route path="/dev/smart-tool-credit-wiring" element={<SmartToolCreditWiring />} />
                    <Route path="/dev/email-automation" element={<EmailAutomation />} />
                    <Route path="/dev/agent-activity" element={<AgentActivityPreview />} />
                    <Route path="/dev/generative-ui-sandbox" element={<GenerativeUISandbox />} />
                  </>
                )}
                <Route path="/unauthorized" element={<Unauthorized />} />
                {/* Keep public help routes mirrored here and in the authenticated route tree above. */}
                <Route path="/help" element={<HelpCenter />} />
                <Route path="/help/guides" element={<Navigate to="/learn" replace />} />
                <Route path="/help/platform-faqs" element={<PlatformFAQs />} />
                <Route path="/help/:collection" element={<HelpCenter />} />
                <Route path="/help/:collection/:article" element={<HelpCenter />} />
                <Route path="/support" element={<Support />} />

                {/* Redirect /signin to /login */}
                <Route path="/signin" element={<Navigate to="/login" replace />} />

                {/* These routes are handled in the AppShell section above */}
              </Routes>
            </Suspense>
          </div>

          {!hideNavAndFooter && <Footer />}
        </>
      )}

      {/* Bearing composer sheet on every authenticated in-app route. Gated to
          mobile and self-excluded from the shell routes, which mount their own. */}
      <GlobalComposerSheet enabled={!!isInternalAppPage} />

      {/* Development info banner - Shows on staging and local dev only */}
      {/* Temporarily disabled for UI design work
      {(import.meta.env.DEV || window.location.hostname === 'adu-lab-uat.web.app') && (
        <DevInfo user={currentUser} />
      )}
      */}
    </>
  );
};

function App() {
  // Capture UTM parameters from the URL on first load (Meta Ads attribution)
  useEffect(() => {
    captureUtmParams();
  }, []);

  // Load Google Maps script globally (needed for questionnaire modal from any page)
  useEffect(() => {
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&libraries=maps,places,marker&v=beta&solution-channel=GMP_QB_placeautocomplete_v1&loading=async`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      console.log('Google Maps script loading globally from App.jsx');
    }
  }, []);

  return (
    <ToastProvider position="top-right">
      <Router>
        <PurchaseConversionTracker />
        <AppContent />
      </Router>
    </ToastProvider>
  );
}

export default App;
